import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "text-white bg-[linear-gradient(135deg,#6366f1_0%,#8b5cf6_50%,#a855f7_100%)] bg-[length:200%_200%] shadow-[0_4px_14px_-2px_rgba(124,58,237,0.35)] hover:bg-[position:100%_0] hover:shadow-[0_6px_20px_-2px_rgba(124,58,237,0.45)] hover:-translate-y-0.5",
        destructive:
          "text-white bg-[linear-gradient(135deg,#f43f5e_0%,#e11d48_100%)] shadow-[0_4px_14px_-2px_rgba(244,63,94,0.30)] hover:shadow-[0_6px_20px_-2px_rgba(244,63,94,0.40)] hover:-translate-y-0.5",
        outline:
          "border border-slate-200 bg-white/70 backdrop-blur-sm shadow-sm hover:bg-gradient-to-br hover:from-indigo-50 hover:to-violet-50 hover:border-indigo-300 hover:text-indigo-700",
        secondary:
          "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-800 shadow-sm hover:from-slate-200 hover:to-slate-300",
        ghost: "hover:bg-gradient-to-br hover:from-indigo-50 hover:to-violet-50 hover:text-indigo-700",
        link: "text-indigo-600 underline-offset-4 hover:underline hover:text-indigo-700",
        success:
          "text-white bg-[linear-gradient(135deg,#10b981_0%,#14b8a6_100%)] shadow-[0_4px_14px_-2px_rgba(16,185,129,0.30)] hover:shadow-[0_6px_20px_-2px_rgba(16,185,129,0.40)] hover:-translate-y-0.5",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-11 rounded-lg px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// Async-aware Button. When `onClick` returns a Promise, the button auto-disables
// and shows a spinner until the promise settles — every page that hands an
// async handler to <Button> gets loading state for free, no per-page wiring.
// Callers can still pass `loading` explicitly to override (handy when the
// async work lives outside onClick — e.g. form submit kicks off a state
// machine elsewhere) or `disabled` to gate by external state.
//   asChild=true uses Radix Slot, which requires a single child — we skip the
//   spinner there since we can't safely inject a sibling element.
const Button = React.forwardRef(({
  className, variant, size, asChild = false,
  onClick, loading: loadingProp, disabled, children, ...props
}, ref) => {
  const [autoLoading, setAutoLoading] = React.useState(false);
  const loading = loadingProp ?? autoLoading;

  const handleClick = React.useCallback((e) => {
    if (!onClick) return;
    let result;
    try { result = onClick(e); }
    catch (err) {
      // Sync throw — let it surface, just don't enter loading state.
      throw err;
    }
    if (result && typeof result.then === 'function') {
      setAutoLoading(true);
      // Use .then/.catch to swallow rejections here — we only care about
      // settle timing. The original promise is still observable to the
      // caller (we don't return result), but typical onClick handlers
      // don't await their own return.
      Promise.resolve(result).finally(() => setAutoLoading(false));
    }
  }, [onClick]);

  const Comp = asChild ? Slot : "button"
  // Only inject the spinner when we auto-detected the loading state. When the
  // caller passed `loading` explicitly they've usually already swapped their
  // children to a "Saving…" string or similar — adding a second indicator
  // looks busy.
  const showAutoSpinner = autoLoading && loadingProp === undefined && !asChild;
  // When asChild=true, Comp is Radix Slot which enforces React.Children.only.
  // The native-button branch renders `{spinner && ...}{children}` which JSX
  // turns into a 2-element array, blowing up Slot's invariant. So branch the
  // render: asChild gets ONLY the single child, native button gets the array
  // (and the optional spinner).
  if (asChild) {
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        onClick={onClick ? handleClick : undefined}
        {...props}
      >
        {children}
      </Comp>
    );
  }
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || loading}
      onClick={onClick ? handleClick : undefined}
      {...props}
    >
      {showAutoSpinner && <Loader2 className="animate-spin" />}
      {children}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
